import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_24eb0a6d-9d2f-4d94-a017-bc81aa56f600/artifacts/r7716m4i_Sparkle%20Grant%20Solutions%20-%201.png';

const InvoiceForm = ({ mode = 'create' }) => {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    invoice_number: '',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    client_name: '',
    client_address: '',
    line_items: [{ description: '' }],
    total_amount: 0,
    deposit_amount: 0,
    remainder_amount: 0,
    payment_terms: 'Deposit due to start project. Final balance due upon completion.',
    payment_method: 'Zelle, Cash, or Check',
    notes: '',
    status: 'pending'
  });

  useEffect(() => {
    if (mode === 'edit' && invoiceNumber) {
      fetchInvoice();
    }
  }, [mode, invoiceNumber]);

  useEffect(() => {
    // Auto-calculate remainder when total or deposit changes
    const remainder = formData.total_amount - formData.deposit_amount;
    if (remainder !== formData.remainder_amount) {
      setFormData(prev => ({ ...prev, remainder_amount: remainder }));
    }
  }, [formData.total_amount, formData.deposit_amount]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceNumber}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index].description = value;
    setFormData(prev => ({ ...prev, line_items: newLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '' }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newLineItems = formData.line_items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, line_items: newLineItems }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validation
    if (!formData.invoice_number) {
      toast({
        title: 'Error',
        description: 'Invoice number is required',
        variant: 'destructive'
      });
      setSubmitting(false);
      return;
    }

    if (!formData.client_name) {
      toast({
        title: 'Error',
        description: 'Client name is required',
        variant: 'destructive'
      });
      setSubmitting(false);
      return;
    }

    if (formData.line_items.some(item => !item.description.trim())) {
      toast({
        title: 'Error',
        description: 'All line items must have descriptions',
        variant: 'destructive'
      });
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        deposit_amount: parseFloat(formData.deposit_amount),
        remainder_amount: parseFloat(formData.remainder_amount)
      };

      if (mode === 'create') {
        await axios.post(`${API}/invoices`, payload);
        toast({
          title: 'Success',
          description: 'Invoice created successfully'
        });
      } else {
        await axios.put(`${API}/invoices/${invoiceNumber}`, payload);
        toast({
          title: 'Success',
          description: 'Invoice updated successfully'
        });
      }

      navigate('/');
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save invoice',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Sparkle Grant Solutions" className="h-12 w-auto" />
            <h1 className="text-3xl font-bold text-gray-900">
              {mode === 'create' ? 'Create New Invoice' : 'Edit Invoice'}
            </h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Invoice Number and Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice_number">Invoice Number *</Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                    placeholder="e.g., 108"
                    disabled={mode === 'edit'}
                    required
                    data-testid="invoice-number-input"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    placeholder="e.g., January 27, 2025"
                    required
                    data-testid="invoice-date-input"
                  />
                </div>
              </div>

              {/* Client Information */}
              <div>
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder="e.g., Creative Marketing Group, Of Delaware, LLC"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div>
                <Label htmlFor="client_address">Client Address (Optional)</Label>
                <Input
                  id="client_address"
                  value={formData.client_address}
                  onChange={(e) => handleInputChange('client_address', e.target.value)}
                  placeholder="Client address"
                  data-testid="client-address-input"
                />
              </div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Services / Line Items *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, e.target.value)}
                        placeholder={`Service description ${index + 1}`}
                        className="flex-1"
                        rows={2}
                        required
                        data-testid={`line-item-${index}`}
                      />
                      {formData.line_items.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          data-testid={`remove-line-item-${index}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Amounts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-purple-50 rounded-lg">
                <div>
                  <Label htmlFor="total_amount">Total Amount *</Label>
                  <Input
                    id="total_amount"
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => handleInputChange('total_amount', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    required
                    data-testid="total-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="deposit_amount">Deposit Amount *</Label>
                  <Input
                    id="deposit_amount"
                    type="number"
                    step="0.01"
                    value={formData.deposit_amount}
                    onChange={(e) => handleInputChange('deposit_amount', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    required
                    data-testid="deposit-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="remainder_amount">Remainder (Auto)</Label>
                  <Input
                    id="remainder_amount"
                    type="number"
                    step="0.01"
                    value={formData.remainder_amount}
                    disabled
                    className="bg-gray-100"
                    data-testid="remainder-amount-display"
                  />
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Textarea
                  id="payment_terms"
                  value={formData.payment_terms}
                  onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                  rows={2}
                  data-testid="payment-terms-input"
                />
              </div>

              {/* Payment Method */}
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Input
                  id="payment_method"
                  value={formData.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                  placeholder="e.g., Zelle, Cash, or Check"
                  data-testid="payment-method-input"
                />
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes or terms..."
                  data-testid="notes-input"
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  data-testid="save-invoice-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {submitting ? 'Saving...' : mode === 'create' ? 'Create Invoice' : 'Update Invoice'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvoiceForm;
